import React, { useEffect, useRef } from "react";
import useOnWindowRefresh from "../utils/useOnWindowRefresh";
import useLogin from "./useLogin";
import useJWTCookie from "./useJWTCookie";
import { defaultUser, useUserStore } from "../../stores/useUserStore";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { IUser } from "../../api/IUser";
import useAxios from "./useAxios";
import { useTimeout } from "ahooks";
import useTimeoutEffect from "../utils/useTimeoutEffect";

const useRefreshUser = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const locationPathname = location.pathname;

    const meetingId = (useParams() as { meetingId?: string } | undefined)
        ?.meetingId;

    const { user, clear: clearUser, setUser } = useUserStore();

    const { getJwt, setJwt, remove: clearJwt } = useJWTCookie();
    const { error, loading, get } = useAxios();

    const isDefaultUser = user.id === defaultUser.id;
    
    // 防抖：记录上一次 refresh 执行的时间，防止短时间内重复调用
    const lastRefreshTimeRef = useRef<number>(0);
    const REFRESH_DEBOUNCE_MS = 1000; // 最少间隔1秒

    // ✅ 新增：检查是否在登录页面
    const isLoginPage = locationPathname === "/login" || locationPathname.startsWith("/login/");
    
    // ✅ 新增：isMounted 检查防止内存泄漏
    const isMountedRef = useRef(true);
    
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    
    useOnWindowRefresh(() => {
        if (!isLoginPage) {
            refresh();
        }
    });

    // 在页面加载时立即尝试恢复用户状态（页面刷新时）
    useEffect(() => {
        if (!isLoginPage) {
            console.log("[useRefreshUser] Initial mount, attempting to restore user from JWT");
            refresh();
        }
    }, [isLoginPage]);

    useTimeoutEffect(
        () => {
            if (!isLoginPage) {
                refresh();
            }
        },
        [locationPathname],
        1500
    );

    const toLogin = () => {
        let to = meetingId ? `/login/${meetingId}` : "/login";
        clearJwt().then(() => {
            // ce6d6e8f-3150-4053-81f0-214214c83dae
            clearUser();
            navigate(to);
        });
    };

    const refresh = async () => {
        // ✅ 新增：如果在登录页面，直接返回
        if (isLoginPage) {
            console.log("[useRefreshUser] Skipping refresh - on login page");
            return;
        }
        
        // 防抖检查：如果距离上次 refresh 不到 1 秒，则跳过
        const now = Date.now();
        if (now - lastRefreshTimeRef.current < REFRESH_DEBOUNCE_MS) {
            console.log(`[useRefreshUser] Debounced refresh - last call was ${now - lastRefreshTimeRef.current}ms ago`);
            return;
        }
        lastRefreshTimeRef.current = now;

        const [jwtToken, expirationDateString] = await getJwt();

        console.log(`[useRefreshUser] refresh() called - token exists: ${!!jwtToken}, isDefaultUser: ${isDefaultUser}`);

        const tokenExpired = () => {
            // ✅ 正确的检查方式：先 parseInt，再检查结果是否为 NaN
            if (!expirationDateString) return true;
            
            const expirationDateNumber = parseInt(expirationDateString);
            
            if (isNaN(expirationDateNumber)) return true;

            if (new Date().getTime() < expirationDateNumber) return false;

            return true;
        };

        const getToken = () => {
            let token = user.jiraToken;

            if (jwtToken && jwtToken.length > 0) return jwtToken;

            if (token && token.length > 0) return token;

            return undefined;
        };

        const callApi = async (token: string) => {
            console.log("[useRefreshUser] Calling /api/user/refresh");
            let res = await get<IUser>(`/api/user/refresh`, token);

            // ✅ 仅在组件仍然挂载时才更新状态
            if (!isMountedRef.current) return;

            if (!res) {
                console.log("[useRefreshUser] User refresh failed, redirecting to login");
                return toLogin();
            }

            console.log("[useRefreshUser] User refreshed successfully");
            // ✅ 关键修复：await 确保 JWT 已保存
            await setJwt(res.refreshToken);
            
            // 关键优化：只在用户ID改变时才更新 store，避免不必要的重新渲染和 SignalR 连接重置
            if (user.id !== res.id) {
                console.log("[useRefreshUser] User ID changed, updating store");
                setUser(res);
            } else {
                console.log("[useRefreshUser] User ID unchanged, skipping store update");
            }
        };

        let token = getToken();

        if (!token) {
            console.log("[useRefreshUser] No token found, redirecting to login");
            return toLogin();
        }

        if (tokenExpired()) {
            console.log("[useRefreshUser] Token expired, redirecting to login");
            return toLogin();
        }

        if (!isDefaultUser) {
            console.log("[useRefreshUser] User already restored, skipping refresh");
            return;
        }

        await callApi(token);
    };
};

export default useRefreshUser;
